import React from 'react';
import { Typography, Menu, MenuItem, ListItemIcon, Tooltip, Divider, Dialog, DialogTitle, DialogContent, Box, IconButton, Modal, Fade, TextField, Button, CircularProgress } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import FolderIcon from '@mui/icons-material/Folder';
import StorageIcon from '@mui/icons-material/Storage';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { TabIndex } from '../navigation';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { getPlanTier, getPlanColor } from '../utils/subscriptionUtils';
import OrganizationUpgradeModal from './OrganizationUpgradeModal';
import { useUnifiedWeb3Wallet, WalletType } from '../contexts/unified_wallet/UnifiedWeb3WalletContext';
import { WorkspaceStatsService } from '../services/WorkspaceStatsService';
import { toastError } from '../utils/toast';
import { SystemEventTypes } from '@flashbacktech/flashbackclient/api';
import { usePermissions } from '../hooks/usePermissions';

// Import icons
import docsSvg from '../assets/icons-home/docs.svg';
import supportSvg from '../assets/icons-home/support.svg';
import bellSvg from '../assets/icons-home/bell.svg';
import docsLinkSvg from '../assets/sidebar-icons/docs-link.svg';
import checkNotifsIcon from '../assets/notifications/check-notifs.svg';
import reloadNotifsIcon from '../assets/notifications/reload-notifs.svg';
import warningNotifsIcon from '../assets/notifications/warning-notifs.svg';
import stellarLogo from '../assets/icons-cloud/stellar-xlm-logo.svg';
import suiLogo from '../assets/icons-cloud/sui-sui-logo.svg';
import { toast } from 'react-toastify';

// Support Modal Component
interface SupportModalProps {
  open: boolean;
  onClose: () => void;
  apiClient: any;
  withTokenRefresh: any;
  userEmail: string;
}

const SupportModal: React.FC<SupportModalProps> = ({ open, onClose, apiClient, withTokenRefresh, userEmail }) => {
  const [email, setEmail] = React.useState(userEmail);
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [attachment, setAttachment] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const MAX_CHARS = 1000;
  const MAX_FILE_SIZE = 25 * 1024 * 1024;

  React.useEffect(() => {
    if (userEmail) setEmail(userEmail);
  }, [userEmail]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileError('');
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setFileError('File size exceeds 25 MB limit');
        setAttachment(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        setAttachment(file);
      }
    }
  };

  const handleRemoveFile = () => {
    setAttachment(null);
    setFileError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClear = () => {
    setSubject('');
    setMessage('');
    handleRemoveFile();
  };

  const handleSubmit = async () => {
    if (!email || !subject || !message) {
      toast.error('Please fill in all fields');
      return;
    }
    if (!email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (message.length > MAX_CHARS) {
      toast.error(`Message exceeds ${MAX_CHARS} characters`);
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('subject', subject);
      formData.append('message', message);
      if (attachment) formData.append('attachment', attachment);

      const response = await withTokenRefresh(() => apiClient.sendFeedbackEmail(formData));
      if ((response as any)?.success) {
        toast.success('Your message has been sent successfully');
        handleClear();
        onClose();
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch {
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Fade in={open}>
        <Box sx={{ width: '100%', maxWidth: 480, bgcolor: '#FFFFFF', borderRadius: '16px', boxShadow: '0px 20px 60px rgba(0,0,0,0.15)', outline: 'none', overflow: 'hidden' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.5, borderBottom: '1px solid #E5E7EB' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#617BFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <SendIcon sx={{ fontSize: 20, color: 'white' }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#101828' }}>Contact Support</Typography>
                <Typography sx={{ fontSize: '12px', color: '#667085' }}>We'll get back to you shortly</Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} sx={{ width: 32, height: 32, borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F3F4F6' } }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          {/* Form */}
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Email */}
            <Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#344054', mb: 0.75 }}>Email</Typography>
              <TextField
                fullWidth
                size="small"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                disabled={isSubmitting}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#FAFAFA', '& fieldset': { borderColor: '#E5E7EB' }, '&:hover fieldset': { borderColor: '#617BFF' }, '&.Mui-focused fieldset': { borderColor: '#617BFF' } } }}
              />
            </Box>

            {/* Subject */}
            <Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#344054', mb: 0.75 }}>Subject</Typography>
              <TextField
                fullWidth
                size="small"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief description"
                disabled={isSubmitting}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#FAFAFA', '& fieldset': { borderColor: '#E5E7EB' }, '&:hover fieldset': { borderColor: '#617BFF' }, '&.Mui-focused fieldset': { borderColor: '#617BFF' } } }}
              />
            </Box>

            {/* Message */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#344054' }}>Message</Typography>
                <Typography sx={{ fontSize: '11px', color: message.length > MAX_CHARS * 0.9 ? '#F59E0B' : '#9CA3AF' }}>{message.length}/{MAX_CHARS}</Typography>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={message}
                onChange={(e) => e.target.value.length <= MAX_CHARS && setMessage(e.target.value)}
                placeholder="Describe your issue or feedback..."
                disabled={isSubmitting}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#FAFAFA', '& fieldset': { borderColor: '#E5E7EB' }, '&:hover fieldset': { borderColor: '#617BFF' }, '&.Mui-focused fieldset': { borderColor: '#617BFF' } } }}
              />
            </Box>

            {/* Attachment */}
            <Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#344054', mb: 0.75 }}>Attachment (optional)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<AttachFileIcon sx={{ fontSize: 16 }} />}
                  disabled={isSubmitting}
                  sx={{ borderRadius: '8px', borderColor: '#E5E7EB', color: '#667085', textTransform: 'none', fontSize: '13px', '&:hover': { borderColor: '#617BFF', bgcolor: '#F0F2FF' } }}
                >
                  Choose file
                  <input type="file" hidden ref={fileInputRef} onChange={handleFileChange} />
                </Button>
                {attachment && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, bgcolor: '#F3F4F6', borderRadius: '6px' }}>
                    <Typography sx={{ fontSize: '12px', color: '#344054', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{attachment.name}</Typography>
                    <IconButton size="small" onClick={handleRemoveFile} sx={{ p: 0.25 }}><CloseIcon sx={{ fontSize: 14, color: '#9CA3AF' }} /></IconButton>
                  </Box>
                )}
              </Box>
              {fileError && <Typography sx={{ fontSize: '11px', color: '#EF4444', mt: 0.5 }}>{fileError}</Typography>}
              <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.5 }}>Max 25 MB</Typography>
            </Box>
          </Box>

          {/* Footer */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, px: 3, py: 2.5, borderTop: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
            <Button onClick={handleClear} disabled={isSubmitting} sx={{ borderRadius: '8px', textTransform: 'none', color: '#667085', fontSize: '13px', fontWeight: 600, px: 2 }}>Clear</Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !email || !subject || !message}
              variant="contained"
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
              sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: '#617BFF', fontSize: '13px', fontWeight: 600, px: 2.5, '&:hover': { bgcolor: '#5169E8' }, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' } }}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

interface HeaderProps {
  sidebarCollapsed: boolean;
  handleTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  tabValue: number;
}

const Header: React.FC<HeaderProps> = ({ 
  sidebarCollapsed, 
  handleTabChange,
  tabValue,
}) => {
  const { globalData, handleSignOut, setSelectedWorkspace, authState, refreshGlobalData } = useAuth();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Initialize WorkspaceStatsService with apiClient and withTokenRefresh
  React.useEffect(() => {
    if (apiClient) {
      WorkspaceStatsService.initialize(apiClient, withTokenRefresh);
    }
  }, [apiClient, withTokenRefresh]);
  const { 
    walletType, 
    isConnected, 
    walletAddress, 
    connectWallet, 
    disconnectWallet, 
    loading: walletLoading, 
    formatPublicKey,
    switchWalletType 
  } = useUnifiedWeb3Wallet();
  
  // Detect if we're on Hub page or a Hub-level product (like Organization, Workspace)
  const isHubPage = location.pathname.startsWith('/hub');
  const isHubProduct = location.pathname.startsWith('/organization'); // Organization is a Hub-level product
  const isWorkspacePage = location.pathname.startsWith('/workspace'); // Workspace pages
  const isChatPage = location.pathname.startsWith('/chat'); // Chat Engine page
  const isDepinHubPath = location.pathname.startsWith('/depin-hub'); // ISP Market pages

  // Header should be full width on all pages
  const isFullWidthHeader = true;
  
  // Check if current tab is a ISP Market page (for non-wallet UI elements)
  const isDepinHubPage = 
    tabValue === TabIndex.StorageUnitsDePIN || 
    tabValue === TabIndex.Marketplace || 
    tabValue === TabIndex.MyDeals ||
    tabValue === TabIndex.Hub ||
    tabValue === TabIndex.StellarBuckets ||
    tabValue === TabIndex.StellarMarketplace ||
    tabValue === TabIndex.StellarMyDeals;
  
  // Use URL path for wallet elements to prevent flash during navigation
  // Hide Web3 wallet button from header across all services as requested
  const showWalletElements = false;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [workspaceAnchorEl, setWorkspaceAnchorEl] = React.useState<null | HTMLElement>(null);
  const [walletAnchorEl, setWalletAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const workspaceMenuOpen = Boolean(workspaceAnchorEl);
  const walletMenuOpen = Boolean(walletAnchorEl);
  
  // All workspaces dialog state
  const [showAllWorkspaces, setShowAllWorkspaces] = React.useState(false);
  
  // Notifications panel state
  const [showNotifications, setShowNotifications] = React.useState(false);
  
  // Upgrade modal state for individual accounts
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);
  const [upgradeFeature, setUpgradeFeature] = React.useState<'organization' | 'chat'>('organization');
  const [eventsLoading, setEventsLoading] = React.useState(false);
  const [systemEvents, setSystemEvents] = React.useState<SystemEventTypes.SystemEventResponse[]>([]);
  const notificationsButtonRef = React.useRef<HTMLButtonElement>(null);
  
  // Workspace stats
  const [workspaceStats, setWorkspaceStats] = React.useState(() => {
    // Charger immédiatement depuis le cache au montage
    const workspaceId = localStorage.getItem('selectedWorkspaceId') || '';
    if (workspaceId) {
      const cached = WorkspaceStatsService.getStatsFromCache(workspaceId);
      if (cached) {
        return {
          bucketsCount: cached.bucketsCount,
          reposCount: cached.reposCount,
          violationsCount: cached.violationsCount,
          aiLlmsCount: cached.aiLlmsCount,
          aiApiKeysCount: cached.aiApiKeysCount,
        };
      }
    }
    return {
      bucketsCount: 0,
      reposCount: 0,
      violationsCount: 0,
      aiLlmsCount: 0,
      aiApiKeysCount: 0,
    };
  });
  
  // Get quota data from localStorage (same as Sidebar)
  const [quotaData, setQuotaData] = React.useState<any>(() => {
    const saved = localStorage.getItem('quotaData');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // Ignore parse errors
      }
    }
    return {};
  });
  
  // Listen for quota updates from Sidebar
  React.useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('quotaData');
      if (saved) {
        try {
          setQuotaData(JSON.parse(saved));
        } catch {
          // Ignore parse errors
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('quotaUpdated', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('quotaUpdated', handleStorageChange);
    };
  }, []);
  
  // Dispatch custom events for modal management
  const openOrganizationModal = (section: string, showHover: boolean) => {
    window.dispatchEvent(new CustomEvent('openOrganizationModal', { 
      detail: { section, showHover } 
    }));
  };
  
  const openProfileModal = (section: string) => {
    window.dispatchEvent(new CustomEvent('openProfileModal', { 
      detail: { section } 
    }));
  };

  // Helper: get notification icon based on event type
  const getNotificationIcon = (event: string): string => {
    const evt = event.toLowerCase();
    if (evt === 'created') return checkNotifsIcon;
    if (evt === 'updated' || evt === 'changed') return reloadNotifsIcon;
    if (evt === 'deleted' || evt === 'invalid') return warningNotifsIcon;
    return checkNotifsIcon;
  };

  const { isOrgManager } = usePermissions();
  const currentUserId = globalData.userProfile?.id || '';

  const loadSystemEvents = React.useCallback(async () => {
    if (!authState.token) return;
    setEventsLoading(true);
    try {
      const data: SystemEventTypes.SystemEventQueryRequest = {
        skip: 0,
        take: 50,
      };
      const res = await withTokenRefresh(() => apiClient.getSystemEvents(data));
      if (res && Array.isArray(res.events)) {
        setSystemEvents(res.events);
      }
    } catch (err) {
      /* ignore */
    } finally {
      setEventsLoading(false);
    }
  }, [authState.token, withTokenRefresh, apiClient]);

  // Load events on mount and when notifications panel opens
  React.useEffect(() => {
    loadSystemEvents();
  }, [loadSystemEvents]);

  // Reload events when notifications panel opens
  React.useEffect(() => {
    if (showNotifications) {
      loadSystemEvents();
    }
  }, [showNotifications, loadSystemEvents]);

  // Support modal state
  const [showSupportModal, setShowSupportModal] = React.useState(false);

  // Load workspace stats using centralized service
  const loadWorkspaceStats = React.useCallback(async (forceRefresh = false) => {
    // Skip if no token, no workspace selected, or global data not ready
    if (!authState?.token || !globalData.selectedWorkspaceId || !globalData.isReady) {
      setWorkspaceStats({
        bucketsCount: 0,
        reposCount: 0,
        violationsCount: 0,
        aiLlmsCount: 0,
        aiApiKeysCount: 0,
      });
      return;
    }
    
    try {
      const stats = await WorkspaceStatsService.loadWorkspaceStats(
        globalData.selectedWorkspaceId,
        forceRefresh // Use forceRefresh parameter
      );
      
      setWorkspaceStats({
        bucketsCount: stats.bucketsCount,
        reposCount: stats.reposCount,
        violationsCount: stats.violationsCount,
        aiLlmsCount: stats.aiLlmsCount,
        aiApiKeysCount: stats.aiApiKeysCount,
      });
    } catch (err) {
      console.error('[Header] Error loading workspace stats:', err);
    }
  }, [authState?.token, globalData.selectedWorkspaceId, globalData.isReady]);

  // Load stats on mount and when workspace changes
  React.useEffect(() => {
    // Immediate load on mount (use cache if available)
    loadWorkspaceStats();
  }, [loadWorkspaceStats]);

  // Reload stats 3 seconds after mount to handle timing issues
  React.useEffect(() => {
    const timer = setTimeout(() => {
      console.log('[Header] Auto-reload stats after 3s to handle timing issues');
      loadWorkspaceStats(true); // Force refresh to get latest data
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [loadWorkspaceStats]);

  // Listen for workspace changes and stats updates
  React.useEffect(() => {
    let debounceTimer: number | null = null;
    
    const onWorkspaceChanged = () => {
      // Clear previous timer
      if (debounceTimer) {
        window.clearTimeout(debounceTimer);
      }
      
      // Set new timer with delay to avoid rapid calls
      debounceTimer = window.setTimeout(() => {
        loadWorkspaceStats();
      }, 400);
    };
    
    // Écouter les mises à jour du service centralisé
    const unsubscribe = WorkspaceStatsService.onStatsUpdate((stats) => {
      setWorkspaceStats({
        bucketsCount: stats.bucketsCount,
        reposCount: stats.reposCount,
        violationsCount: stats.violationsCount,
        aiLlmsCount: stats.aiLlmsCount,
        aiApiKeysCount: stats.aiApiKeysCount,
      });
    });
    
    window.addEventListener('workspaceChanged', onWorkspaceChanged);
    window.addEventListener('bucketsUpdated', onWorkspaceChanged);
    
    return () => {
      if (debounceTimer) {
        window.clearTimeout(debounceTimer);
      }
      unsubscribe();
      window.removeEventListener('workspaceChanged', onWorkspaceChanged);
      window.removeEventListener('bucketsUpdated', onWorkspaceChanged);
    };
  }, [loadWorkspaceStats]);

  // Close notifications when clicking outside
  React.useEffect(() => {
    if (!showNotifications) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.notifications-panel-header') && !target.closest('[title="Notifications"]')) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleWorkspaceClick = (event: React.MouseEvent<HTMLElement>) => {
    setWorkspaceAnchorEl(event.currentTarget);
  };

  const handleWorkspaceClose = () => {
    setWorkspaceAnchorEl(null);
  };

  const handleWorkspaceChange = (workspaceId: string) => {
    // Update context (which handles localStorage and events automatically)
    setSelectedWorkspace(workspaceId);
    
    // Reload stats after a few seconds to ensure data is loaded
    setTimeout(() => {
      console.log('[Header] Reloading workspace stats after workspace change');
      loadWorkspaceStats(true);
    }, 2000);
    
    handleWorkspaceClose();
  };

  // Chat environment state (from Chat component selection)
  const [chatEnvironment, setChatEnvironment] = React.useState<{
    workspaceName: string;
    repoName: string;
  }>(() => {
    const saved = localStorage.getItem('chatEnvironment');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return { workspaceName: parsed.workspaceName || '', repoName: parsed.repoName || '' };
      } catch { /* ignore */ }
    }
    return { workspaceName: '', repoName: '' };
  });

  // Listen for chat environment changes
  React.useEffect(() => {
    const handleChatEnvChange = () => {
      const saved = localStorage.getItem('chatEnvironment');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setChatEnvironment({ workspaceName: parsed.workspaceName || '', repoName: parsed.repoName || '' });
        } catch { /* ignore */ }
      }
    };
    window.addEventListener('chatEnvironmentChanged', handleChatEnvChange);
    return () => window.removeEventListener('chatEnvironmentChanged', handleChatEnvChange);
  }, []);

  // Get the current workspace name
  const getWorkspaceName = () => {
    const workspace = globalData.workspaces.find(
      (w: any) => w.id === globalData.selectedWorkspaceId
    );
    return workspace?.name || 'Select Workspace';
  };

  // Get chat environment names for header display
  const getChatWorkspaceName = () => chatEnvironment.workspaceName || getWorkspaceName();
  const getChatRepoName = () => chatEnvironment.repoName || 'Repository';

  // Get user email
  const getUserEmail = () => {
    return globalData.userProfile?.email || 'No email available';
  };

  // Handle wallet button click
  const handleWalletClick = (event: React.MouseEvent<HTMLElement>) => {
    // Always open menu - user can choose network and connect from there
    setWalletAnchorEl(event.currentTarget);
  };

  const handleWalletMenuClose = () => {
    setWalletAnchorEl(null);
  };

  const handleDisconnectWallet = () => {
    try {
      const result = disconnectWallet();
      if (result.success) {
        console.log('Wallet disconnected successfully');
      }
    } catch (error) {
      console.error('Error disconnecting wallet:', error);
    }
    handleWalletMenuClose();
  };

  const handleSwitchWalletType = async (type: WalletType) => {
    // If connected and switching to a different type, disconnect current wallet first
    if (isConnected && walletType !== type) {
      disconnectWallet();
    }
    
    // Switch the wallet type
    switchWalletType(type);
    
    // Always connect directly when clicking on a wallet type
    handleWalletMenuClose();
    try {
      const result = await connectWallet(type);
      if (!result.success) {
        console.error('Failed to connect wallet:', result.error);
        if (result.error && result.error.includes('No Sui wallets found')) {
          toastError('SUI Wallet not found. Please install SUI wallet browser extension');
        } else if (result.error) {
          toastError(result.error);
        }
      }
    } catch (error) {
      console.error('Error connecting to wallet:', error);
    }
  };

  const handleConnectWallet = async () => {
    handleWalletMenuClose();
    try {
      const result = await connectWallet(walletType);
      if (!result.success) {
        console.error('Failed to connect wallet:', result.error);
        
        // Show user-friendly error message for Sui wallet not found
        if (result.error && result.error.includes('No Sui wallets found')) {
          toastError('SUI Wallet not found. Please install SUI wallet browser extension');
        } else if (result.error) {
          // Show generic error for other connection failures
          toastError(result.error);
        }
      }
    } catch (error) {
      console.error('Error connecting wallet:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to connect wallet';
      toastError(errorMessage);
    }
  };

  // Get user initials
  const getUserInitials = () => {
    const name = authState?.user?.name || globalData.userProfile?.name || 'U';
    return name.charAt(0).toUpperCase();
  };

  return (
    <div
      className={`fixed top-0 right-0 z-40 transition-all duration-300 ${
        isFullWidthHeader ? 'left-0' : (sidebarCollapsed ? 'lg:left-0' : 'lg:left-[296px]')
      }`}
      style={{
        height: '64px',
        backgroundColor: '#FFFFFF',
        borderBottom: '1px solid #E9EAEB',
      }}
    >
      <div className={`h-full flex items-center justify-between transition-all duration-300 pr-4 sm:pr-6 lg:pr-8 ${
        isFullWidthHeader 
          ? 'pl-6 sm:pl-8 lg:pl-8' 
          : (sidebarCollapsed ? 'pl-16 sm:pl-20 lg:pl-16' : 'pl-16 sm:pl-20 lg:pl-8')
      }`}>
        {/* Left: Back button + Logo on Hub/Organization/Chat pages, Workspace selector on other pages */}
        <div className="flex items-center gap-3">
          {(isHubPage || isHubProduct) ? (
            /* Back button + Flashback Logo for Hub/Organization pages */
            <div className="flex items-center gap-3">
              {/* Back Button - Only show on sub-pages (not on Hub main page) */}
              {!isHubPage && (
                <Tooltip 
                  title="Main" 
                  arrow
                  placement="bottom"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: '#1F2937',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 500,
                        padding: '6px 12px',
                        borderRadius: '6px',
                        '& .MuiTooltip-arrow': { color: '#1F2937' },
                      },
                    },
                  }}
                >
                  <button
                    onClick={() => handleTabChange({} as React.SyntheticEvent, TabIndex.Hub)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      border: '1px solid #E5E7EB',
                      backgroundColor: 'transparent',
                      color: '#667085',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#F0F2FF';
                      e.currentTarget.style.color = '#617BFF';
                      e.currentTarget.style.borderColor = '#617BFF';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#667085';
                      e.currentTarget.style.borderColor = '#E5E7EB';
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                  </button>
                </Tooltip>
              )}
              <img 
                src="/logoblack.png" 
                alt="Flashback" 
                style={{ height: '32px' }}
              />
              
              {/* On /workspace pages, also show workspace selector after logo */}
              {isWorkspacePage && (
                <>
                  {/* Separator */}
                  <div style={{ width: 1, height: 24, backgroundColor: '#E5E7EB', marginLeft: 8 }} />
                  
                  {/* Unified Workspace Selector - Icon + Label + Name in one clickable element */}
                  <div
                    onClick={handleWorkspaceClick}
                    className="cursor-pointer flex items-center gap-2 px-3 py-1.5 rounded-lg"
                    style={{
                      border: '1px solid #E9EAEB',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#F9FAFB';
                      e.currentTarget.style.borderColor = '#617BFF';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = '#E9EAEB';
                    }}
                  >
                    {/* Workspace Icon */}
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        backgroundColor: '#F0F2FF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#617BFF"
                        strokeWidth="2"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <line x1="9" y1="3" x2="9" y2="21" />
                      </svg>
                    </div>
                    {/* Text content */}
                    <div>
                      <Typography
                        variant="body2"
                        style={{
                          color: '#717680',
                          fontSize: '11px',
                          fontWeight: 500,
                          lineHeight: '14px',
                        }}
                      >
                        Workspace
                      </Typography>
                      <Typography
                        variant="h6"
                        style={{
                          color: '#181D27',
                          fontSize: '14px',
                          fontWeight: globalData.workspaces.length > 1 ? 600 : 400,
                          lineHeight: '18px',
                        }}
                      >
                        {getWorkspaceName()}
                      </Typography>
                    </div>
                    {/* Dropdown chevron */}
                    {globalData.workspaces.length > 1 && (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#667085"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginLeft: 4 }}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    )}
                  </div>

                  {/* Workspace Menu for /workspace pages */}
                  <Menu
                    anchorEl={workspaceAnchorEl}
                    open={workspaceMenuOpen}
                    onClose={handleWorkspaceClose}
                    PaperProps={{
                      sx: {
                        backgroundColor: 'white',
                        border: '1px solid #E9EAEB',
                        mt: 1,
                        minWidth: 200,
                        maxHeight: 400,
                        '& .MuiMenuItem-root': {
                          px: 2,
                          py: 1.5,
                          fontSize: '14px',
                          '&:hover': {
                            backgroundColor: '#F9FAFB',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'transparent',
                            color: '#181D27',
                            fontWeight: 600,
                            '&:hover': {
                              backgroundColor: '#F9FAFB',
                            },
                          },
                        },
                      },
                    }}
                    transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                  >
                    {/* Header with title and reload button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: '1px solid #E9EAEB' }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#717680', textTransform: 'uppercase' }}>
                        Workspaces
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (refreshGlobalData) {
                            await refreshGlobalData(true);
                          }
                        }}
                        sx={{
                          p: 0.5,
                          '&:hover': { backgroundColor: '#F0F2FF' },
                        }}
                        title="Reload workspaces"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M23 4v6h-6" />
                          <path d="M1 20v-6h6" />
                          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                        </svg>
                      </IconButton>
                    </Box>
                    {globalData.workspaces.slice(0, 5).map((w: any) => (
                      <MenuItem
                        key={w.id}
                        onClick={() => handleWorkspaceChange(w.id)}
                        selected={globalData.selectedWorkspaceId === w.id}
                      >
                        {w.name}
                      </MenuItem>
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <MenuItem
                      onClick={() => {
                        handleWorkspaceClose();
                        setShowAllWorkspaces(true);
                      }}
                      sx={{
                        color: '#617BFF',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: '#F0F2FF',
                        },
                      }}
                    >
                      View more
                    </MenuItem>
                  </Menu>
                </>
              )}
            </div>
          ) : isDepinHubPath ? (
            /* ISP Market header - simplified with Connect Wallet */
            <div className="flex items-center gap-3">
              {/* Back Button */}
              <Tooltip 
                title="Main" 
                arrow
                placement="bottom"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#1F2937',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 500,
                      padding: '6px 12px',
                      borderRadius: '6px',
                      '& .MuiTooltip-arrow': { color: '#1F2937' },
                    },
                  },
                }}
              >
                <button
                  onClick={() => handleTabChange({} as React.SyntheticEvent, TabIndex.Hub)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    border: '1px solid #E5E7EB',
                    backgroundColor: 'transparent',
                    color: '#667085',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#F0F2FF';
                    e.currentTarget.style.color = '#617BFF';
                    e.currentTarget.style.borderColor = '#617BFF';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#667085';
                    e.currentTarget.style.borderColor = '#E5E7EB';
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                </button>
              </Tooltip>
              
              {/* Flashback Logo */}
              <img 
                src="/logoblack.png" 
                alt="Flashback" 
                style={{ height: '32px' }}
              />
              
              {/* Separator */}
              <div style={{ width: 1, height: 24, backgroundColor: '#E5E7EB' }} />
              
              {/* Connect Wallet Button - Left side of header */}
              <button
                onClick={handleWalletClick}
                disabled={walletLoading}
                className="px-3 py-2 rounded-lg flex items-center gap-2 transition-all"
                style={{
                  backgroundColor: isConnected ? '#F0F2FF' : '#617BFF',
                  color: isConnected ? '#617BFF' : '#FFFFFF',
                  border: isConnected ? '1px solid #617BFF' : 'none',
                  fontSize: '13px',
                  fontWeight: 600,
                  lineHeight: '18px',
                  cursor: walletLoading ? 'wait' : 'pointer',
                  opacity: walletLoading ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!walletLoading) {
                    e.currentTarget.style.backgroundColor = isConnected ? '#E6E9FF' : '#5A6FE8';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isConnected ? '#F0F2FF' : '#617BFF';
                }}
              >
                {walletLoading ? (
                  'Connecting...'
                ) : isConnected && walletAddress ? (
                  <>
                    <img src={stellarLogo} alt="Stellar" style={{ width: 16, height: 16 }} />
                    <span>{formatPublicKey(walletAddress)}</span>
                  </>
                ) : (
                  <>
                    <img src={stellarLogo} alt="Stellar" style={{ width: 16, height: 16 }} />
                    <span>Connect Wallet</span>
                  </>
                )}
              </button>
            </div>
          ) : isChatPage ? (
            /* Chat Engine header for Chat page */
            <div className="flex items-center gap-3">
              {/* Back Button */}
              <Tooltip 
                title="Main" 
                arrow
                placement="bottom"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#1F2937',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 500,
                      padding: '6px 12px',
                      borderRadius: '6px',
                      '& .MuiTooltip-arrow': { color: '#1F2937' },
                    },
                  },
                }}
              >
                <button
                  onClick={() => handleTabChange({} as React.SyntheticEvent, TabIndex.Hub)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    border: '1px solid #E5E7EB',
                    backgroundColor: 'transparent',
                    color: '#667085',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#F0F2FF';
                    e.currentTarget.style.color = '#617BFF';
                    e.currentTarget.style.borderColor = '#617BFF';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#667085';
                    e.currentTarget.style.borderColor = '#E5E7EB';
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                </button>
              </Tooltip>
              
              {/* Flashback Logo */}
              <img 
                src="/logoblack.png" 
                alt="Flashback" 
                style={{ height: '32px' }}
              />
              
              {/* Separator */}
              <div style={{ width: 1, height: 24, backgroundColor: '#E5E7EB', marginLeft: 8 }} />
              
              {/* Unified Chat Selector - Chat Icon + Workspace/Repo in one clickable element (same pattern as Gateway workspace selector) */}
              <div
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('openChatEnvironmentModal'));
                }}
                className="cursor-pointer flex items-center gap-2 px-3 py-1.5 rounded-lg"
                style={{
                  border: '1px solid #E9EAEB',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F9FAFB';
                  e.currentTarget.style.borderColor = '#617BFF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = '#E9EAEB';
                }}
              >
                {/* Chat Icon */}
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                  </svg>
                </div>
                {/* Text content */}
                <div>
                  <Typography
                    variant="body2"
                    style={{
                      color: '#717680',
                      fontSize: '11px',
                      fontWeight: 500,
                      lineHeight: '14px',
                    }}
                  >
                    Private Chat
                  </Typography>
                  <Typography
                    variant="h6"
                    style={{
                      color: '#181D27',
                      fontSize: '14px',
                      fontWeight: 600,
                      lineHeight: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    {getChatWorkspaceName()}
                    <span style={{ color: '#9CA3AF', fontWeight: 400 }}>/</span>
                    {getChatRepoName()}
                  </Typography>
                </div>
                {/* Dropdown chevron */}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#667085"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginLeft: 4 }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>
          ) : (
            /* Workspace selector for other pages */
            <>
          {/* Back Button */}
          <Tooltip 
            title="Main" 
            arrow
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#1F2937',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: '6px 12px',
                  borderRadius: '6px',
                  '& .MuiTooltip-arrow': { color: '#1F2937' },
                },
              },
            }}
          >
            <button
              onClick={() => handleTabChange({} as React.SyntheticEvent, TabIndex.Hub)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: 8,
                border: '1px solid #E5E7EB',
                backgroundColor: 'transparent',
                color: '#667085',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F0F2FF';
                e.currentTarget.style.color = '#617BFF';
                e.currentTarget.style.borderColor = '#617BFF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#667085';
                e.currentTarget.style.borderColor = '#E5E7EB';
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
            </button>
          </Tooltip>

          {/* Flashback Logo */}
          <img 
            src="/logoblack.png" 
            alt="Flashback" 
            style={{ height: '32px' }}
          />

          {/* Separator */}
          <div style={{ width: 1, height: 24, backgroundColor: '#E5E7EB', marginLeft: 8 }} />

          {/* Unified Workspace Selector - Icon + Label + Name in one clickable element */}
          <div
            onClick={handleWorkspaceClick}
            className="cursor-pointer flex items-center gap-2 px-3 py-1.5 rounded-lg"
            style={{
              border: '1px solid #E9EAEB',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F9FAFB';
              e.currentTarget.style.borderColor = '#617BFF';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = '#E9EAEB';
            }}
          >
            {/* Workspace Icon */}
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 6,
                backgroundColor: '#F0F2FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#617BFF"
                strokeWidth="2"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="9" y1="3" x2="9" y2="21" />
              </svg>
            </div>
            {/* Text content */}
            <div>
              <Typography
                variant="body2"
                style={{
                  color: '#717680',
                  fontSize: '11px',
                  fontWeight: 500,
                  lineHeight: '14px',
                }}
              >
                Workspace
              </Typography>
              <Typography
                variant="h6"
                style={{
                  color: '#181D27',
                  fontSize: '14px',
                  fontWeight: globalData.workspaces.length > 1 ? 600 : 400,
                  lineHeight: '18px',
                }}
              >
                {getWorkspaceName()}
              </Typography>
            </div>
            {/* Dropdown chevron */}
            {globalData.workspaces.length > 1 && (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#667085"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginLeft: 4 }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            )}
          </div>
          
          {/* Workspace Menu */}
          <Menu
            anchorEl={workspaceAnchorEl}
            open={workspaceMenuOpen}
            onClose={handleWorkspaceClose}
            PaperProps={{
              sx: {
                backgroundColor: 'white',
                border: '1px solid #E9EAEB',
                mt: 1,
                minWidth: 200,
                maxHeight: 400,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1.5,
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: '#F9FAFB',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'transparent',
                    color: '#181D27',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                    },
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          >
            {/* Header with title and reload button */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: '1px solid #E9EAEB' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#717680', textTransform: 'uppercase' }}>
                Workspaces
              </Typography>
              <IconButton
                size="small"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (refreshGlobalData) {
                    await refreshGlobalData(true);
                  }
                }}
                sx={{
                  p: 0.5,
                  '&:hover': { backgroundColor: '#F0F2FF' },
                }}
                title="Reload workspaces"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 4v6h-6" />
                  <path d="M1 20v-6h6" />
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                </svg>
              </IconButton>
            </Box>
            {globalData.workspaces.slice(0, 5).map((w: any) => (
              <MenuItem
                key={w.id}
                onClick={() => handleWorkspaceChange(w.id)}
                selected={globalData.selectedWorkspaceId === w.id}
              >
                {w.name}
              </MenuItem>
            ))}
            <Divider sx={{ my: 1 }} />
            <MenuItem
              onClick={() => {
                handleWorkspaceClose();
                setShowAllWorkspaces(true);
              }}
              sx={{
                color: '#617BFF',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#F0F2FF',
                },
              }}
            >
              View more
            </MenuItem>
          </Menu>

            </>
          )}
        </div>

        {/* Right: Action Icons + User Email */}
        <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          {/* 1. Gateway - Navigate to /workspace/home (FIRST) */}
          <Tooltip 
            title="Gateway" 
            arrow
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#1F2937',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: '6px 12px',
                  borderRadius: '6px',
                  '& .MuiTooltip-arrow': {
                    color: '#1F2937',
                  },
                },
              },
            }}
          >
            <button
              onClick={() => {
                navigate('/workspace/home');
                handleTabChange({} as React.SyntheticEvent, TabIndex.Home);
              }}
              className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
              style={{
                backgroundColor: (
                  !location.pathname.startsWith('/depin-hub') && (
                    location.pathname === '/workspace/home' || 
                    tabValue === TabIndex.Home ||
                    tabValue === TabIndex.Dashboard ||
                    tabValue === TabIndex.CloudStorage ||
                    tabValue === TabIndex.StorageUnits ||
                    tabValue === TabIndex.Metrics ||
                    tabValue === TabIndex.FileExplorer ||
                    tabValue === TabIndex.Activity ||
                    tabValue === TabIndex.AIOverview ||
                    tabValue === TabIndex.AILLM ||
                    tabValue === TabIndex.AIStatistics ||
                    tabValue === TabIndex.AIPolicies ||
                    tabValue === TabIndex.AIViolations ||
                    tabValue === TabIndex.BridgeNodes ||
                    tabValue === TabIndex.BridgeNodesPublic ||
                    tabValue === TabIndex.BridgeNodesPrivate ||
                    tabValue === TabIndex.RepoOverview ||
                    tabValue === TabIndex.StorageRepos ||
                    tabValue === TabIndex.APIKeys
                  )
                ) ? '#F0F2FF' : 'transparent',
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke={(
                  !location.pathname.startsWith('/depin-hub') && (
                    location.pathname === '/workspace/home' || 
                    tabValue === TabIndex.Home ||
                    tabValue === TabIndex.Dashboard ||
                    tabValue === TabIndex.CloudStorage ||
                    tabValue === TabIndex.StorageUnits ||
                    tabValue === TabIndex.Metrics ||
                    tabValue === TabIndex.FileExplorer ||
                    tabValue === TabIndex.Activity ||
                    tabValue === TabIndex.AIOverview ||
                    tabValue === TabIndex.AILLM ||
                    tabValue === TabIndex.AIStatistics ||
                    tabValue === TabIndex.AIPolicies ||
                    tabValue === TabIndex.AIViolations ||
                    tabValue === TabIndex.BridgeNodes ||
                    tabValue === TabIndex.BridgeNodesPublic ||
                    tabValue === TabIndex.BridgeNodesPrivate ||
                    tabValue === TabIndex.RepoOverview ||
                    tabValue === TabIndex.StorageRepos ||
                    tabValue === TabIndex.APIKeys
                  )
                ) ? '#617BFF' : '#98A2B3'}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="9" y1="3" x2="9" y2="21" />
              </svg>
            </button>
          </Tooltip>

          {/* 2. Organization - Grayed out for individuals */}
          <Tooltip 
            title={globalData?.hasOrgAccess ? "Organization" : "Organization (Upgrade required)"} 
            arrow
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#1F2937',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: '6px 12px',
                  borderRadius: '6px',
                  '& .MuiTooltip-arrow': {
                    color: '#1F2937',
                  },
                },
              },
            }}
          >
            <button
              onClick={() => {
                if (globalData?.hasOrgAccess) {
                  handleTabChange({} as React.SyntheticEvent, TabIndex.Organization);
                } else {
                  setUpgradeFeature('organization');
                  setShowUpgradeModal(true);
                }
              }}
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                globalData?.hasOrgAccess ? 'hover:bg-gray-100 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer opacity-50'
              }`}
              style={{
                backgroundColor: tabValue === TabIndex.Organization && globalData?.hasOrgAccess ? '#F0F2FF' : 'transparent',
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke={!globalData?.hasOrgAccess ? '#D0D5DD' : (tabValue === TabIndex.Organization ? '#617BFF' : '#98A2B3')}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="16" y="16" width="6" height="6" rx="1" />
                <rect x="2" y="16" width="6" height="6" rx="1" />
                <rect x="9" y="2" width="6" height="6" rx="1" />
                <path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3" />
                <path d="M12 12V8" />
              </svg>
            </button>
          </Tooltip>

          {/* 3. Chat - Grayed out for individuals */}
          <Tooltip 
            title={globalData?.hasOrgAccess ? "Private Chat" : "Private Chat (Upgrade required)"} 
            arrow
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#1F2937',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: '6px 12px',
                  borderRadius: '6px',
                  '& .MuiTooltip-arrow': {
                    color: '#1F2937',
                  },
                },
              },
            }}
          >
            <button
              onClick={() => {
                if (globalData?.hasOrgAccess) {
                  handleTabChange({} as React.SyntheticEvent, TabIndex.Chat);
                } else {
                  setUpgradeFeature('chat');
                  setShowUpgradeModal(true);
                }
              }}
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                globalData?.hasOrgAccess ? 'hover:bg-gray-100 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer opacity-50'
              }`}
              style={{
                backgroundColor: tabValue === TabIndex.Chat && globalData?.hasOrgAccess ? '#F0F2FF' : 'transparent',
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke={!globalData?.hasOrgAccess ? '#D0D5DD' : (tabValue === TabIndex.Chat ? '#617BFF' : '#98A2B3')}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </button>
          </Tooltip>

          {/* 4. ISP Market (DePIN Hub) */}
          <Tooltip 
            title="ISP Market" 
            arrow
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#1F2937',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: '6px 12px',
                  borderRadius: '6px',
                  '& .MuiTooltip-arrow': {
                    color: '#1F2937',
                  },
                },
              },
            }}
          >
            <button
              onClick={() => {
                handleTabChange({} as React.SyntheticEvent, TabIndex.StorageUnitsDePIN);
                navigate('/depin-hub/storage-units');
              }}
              className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
              style={{
                backgroundColor: isDepinHubPage ? '#F0F2FF' : 'transparent',
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke={isDepinHubPage ? '#617BFF' : '#98A2B3'}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="3" />
                <circle cx="5" cy="5" r="2" />
                <circle cx="19" cy="5" r="2" />
                <circle cx="5" cy="19" r="2" />
                <circle cx="19" cy="19" r="2" />
                <line x1="12" y1="9" x2="5" y2="7" />
                <line x1="12" y1="9" x2="19" y2="7" />
                <line x1="12" y1="15" x2="5" y2="17" />
                <line x1="12" y1="15" x2="19" y2="17" />
              </svg>
            </button>
          </Tooltip>

          {/* 5. Connect Wallet - show on all pages so users can connect from header */}
          {showWalletElements && (
            <Tooltip 
              title={isConnected && walletAddress ? formatPublicKey(walletAddress) : "Connect Web3 Wallet"} 
              arrow
              placement="bottom"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#1F2937',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 500,
                    padding: '6px 12px',
                    borderRadius: '6px',
                    maxWidth: 280,
                    wordBreak: 'break-all',
                    '& .MuiTooltip-arrow': { color: '#1F2937' },
                  },
                },
              }}
            >
              <button
                onClick={handleWalletClick}
                disabled={walletLoading}
                className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors"
                style={{
                  backgroundColor: isConnected ? '#F0F2FF' : 'transparent',
                  border: isConnected ? '1px solid #617BFF' : '1px solid #E5E7EB',
                  cursor: walletLoading ? 'wait' : 'pointer',
                  opacity: walletLoading ? 0.7 : 1,
                }}
              >
                <AccountBalanceWalletIcon 
                  sx={{ fontSize: 20, color: isConnected ? '#617BFF' : '#98A2B3' }} 
                />
              </button>
            </Tooltip>
          )}

          {/* 6. Hub / Main button */}
          {isHubProduct ? (
            /* Cloud & AI Gateway button - shown when on a Hub-level product (Organization, etc.) */
            <Tooltip 
              title="Main" 
              arrow
              placement="bottom"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#1F2937',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 500,
                    padding: '6px 12px',
                    borderRadius: '6px',
                    '& .MuiTooltip-arrow': {
                      color: '#1F2937',
                    },
                  },
                },
              }}
            >
              <button
                onClick={() => {
                  // Navigate to Hub page
                  handleTabChange({} as React.SyntheticEvent, TabIndex.Hub);
                }}
                className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#98A2B3"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                </svg>
              </button>
            </Tooltip>
          ) : isHubPage ? (
            /* Already on Hub - show Workspace button to go back */
            <Tooltip 
              title="Back to Cloud & AI Gateway" 
              arrow
              placement="bottom"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#1F2937',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 500,
                    padding: '6px 12px',
                    borderRadius: '6px',
                    '& .MuiTooltip-arrow': {
                      color: '#1F2937',
                    },
                  },
                },
              }}
            >
              <button
                onClick={() => {
                  // Navigate back to Home with the last workspace
                  handleTabChange({} as React.SyntheticEvent, TabIndex.Home);
                  navigate('/home');
                }}
                className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
                style={{
                  backgroundColor: 'transparent',
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#98A2B3"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {/* Workspace/Folder icon */}
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
              </button>
            </Tooltip>
          ) : (
            /* Cloud & AI Gateway button - shown on all other pages */
            <Tooltip 
              title="Main" 
              arrow
              placement="bottom"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#1F2937',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 500,
                    padding: '6px 12px',
                    borderRadius: '6px',
                    '& .MuiTooltip-arrow': {
                      color: '#1F2937',
                    },
                  },
                },
              }}
            >
              <button
                onClick={() => handleTabChange({} as React.SyntheticEvent, TabIndex.Hub)}
                className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
                style={{
                  backgroundColor: tabValue === TabIndex.Hub ? '#F0F2FF' : 'transparent',
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={tabValue === TabIndex.Hub ? '#617BFF' : '#98A2B3'}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                </svg>
              </button>
            </Tooltip>
          )}

          
          {/* Wallet Menu - Only show on ISP Market pages (use URL path to prevent flash) */}
          {(showWalletElements || isDepinHubPath) && (
          <Menu
            anchorEl={walletAnchorEl}
            open={walletMenuOpen}
            onClose={handleWalletMenuClose}
            PaperProps={{
              sx: {
                backgroundColor: '#FFFFFF',
                color: '#181D27',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
                mt: 1,
                minWidth: 260,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1.5,
                  borderRadius: '8px',
                  margin: '4px 8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: '#F0F2FF',
                  },
                },
                '& .MuiListItemIcon-root': {
                  color: '#617BFF',
                  minWidth: 36,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {isConnected && walletAddress && (
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #E5E7EB' }}>
                <Typography sx={{ fontSize: '12px', color: '#6B7280', mb: 0.5 }}>Connected Wallet</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#181D27', wordBreak: 'break-all' }}>
                  {formatPublicKey(walletAddress)}
                </Typography>
              </Box>
            )}
            <Divider sx={{ borderColor: '#E5E7EB', my: 0.5 }} />
            <MenuItem disabled sx={{ opacity: '0.6 !important', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6B7280 !important' }}>
              {isConnected ? 'Switch Network' : 'Select Network'}
            </MenuItem>
            <MenuItem 
              onClick={() => handleSwitchWalletType(WalletType.STELLAR)}
              sx={{
                backgroundColor: walletType === WalletType.STELLAR ? '#F0F2FF' : 'transparent',
              }}
            >
              <ListItemIcon>
                <img src={stellarLogo} alt="Stellar" width="20" height="20" />
              </ListItemIcon>
              Stellar
              {walletType === WalletType.STELLAR && (
                <Typography variant="caption" sx={{ ml: 'auto', fontSize: '10px', color: '#617BFF', fontWeight: 600 }}>
                  {isConnected ? 'Active' : 'Selected'}
                </Typography>
              )}
            </MenuItem>
            {false && (  // remove SUI wallet temporarily
            <MenuItem 
              onClick={() => handleSwitchWalletType(WalletType.SUI)}
              sx={{
                backgroundColor: walletType === WalletType.SUI ? '#F0F2FF' : 'transparent',
              }}
            >
              <ListItemIcon>
                <img src={suiLogo} alt="Sui" width="20" height="20" />
              </ListItemIcon>
              Sui
              {walletType === WalletType.SUI && (
                <Typography variant="caption" sx={{ ml: 'auto', fontSize: '10px', color: '#617BFF', fontWeight: 600 }}>
                  {isConnected ? 'Active' : 'Selected'}
                </Typography>
              )}
            </MenuItem>
            )}
            {isConnected && (
              <>
                <Divider sx={{ borderColor: '#E5E7EB', my: 0.5 }} />
                <MenuItem 
                  onClick={handleDisconnectWallet}
                  sx={{ color: '#DC2626 !important' }}
                >
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" sx={{ color: '#DC2626' }} />
                  </ListItemIcon>
                  Disconnect
                </MenuItem>
              </>
            )}
          </Menu>
          )}
          
          {/* Notifications Panel */}
          {showNotifications && (
            <div
              className="notifications-panel-header fixed z-50 pointer-events-auto"
              style={{
                top: '70px',
                right: notificationsButtonRef.current 
                  ? `${window.innerWidth - notificationsButtonRef.current.getBoundingClientRect().right}px`
                  : '20px',
              }}
            >
              <div
                className="bg-white border border-[#E9EAEB] p-4 shadow-lg overflow-hidden flex flex-col"
                style={{ width: 540, height: 434, borderRadius: 12 }}
              >
                <div className="flex items-center justify-between mb-3 shrink-0">
                  <span style={{ fontWeight: 600, fontSize: '16px', color: '#101828' }}>
                    Notifications
                  </span>
                  <div className="flex items-center gap-2">
                    {systemEvents.some((ev) => ev.showUnread) && (
                      <button
                        type="button"
                        className="text-[12px] text-[#617BFF] hover:underline font-medium"
                        onClick={() => {
                          const unreadEvents = systemEvents.filter((ev) => ev.showUnread);
                          unreadEvents.forEach((ev) => {
                            withTokenRefresh(() => apiClient.markSystemEventAsRead(ev.id)).catch(() => { /* noop */ });
                          });
                          setSystemEvents((prev) =>
                            prev.map((e) => (e.showUnread ? { ...e, showUnread: false } : e))
                          );
                        }}
                      >
                        Mark all as Read
                      </button>
                    )}
                    <button
                      className="text-gray-500 hover:text-gray-700"
                      onClick={() => setShowNotifications(false)}
                      title="Close"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                <div className="flex-1 min-h-0 mt-1">
                  {eventsLoading ? (
                    <div className="text-sm text-[#475467] py-2">Loading…</div>
                  ) : systemEvents.length === 0 ? (
                    <div className="text-sm text-[#475467] py-2">No recent events</div>
                  ) : (
                    <ul className="h-full overflow-auto divide-y divide-[#E9EAEB] pr-1">
                      {systemEvents
                        .filter((ev) => {
                          // Show all unread events (regardless of age)
                          if (ev.showUnread) return true;
                          // Show read events only if < 24h old
                          const age = Date.now() - new Date(ev.timestamp).getTime();
                          return age < 24 * 60 * 60 * 1000;
                        })
                        .map((ev) => (
                          <li
                            key={ev.id}
                            className={`py-2.5 px-2 transition-all duration-200 rounded-lg group ${
                              ev.showUnread
                                ? 'bg-[rgba(97,123,255,0.06)] border-l-2 border-[#617BFF]'
                                : 'hover:bg-[#FAFAFA]'
                            }`}
                            onMouseEnter={() => {
                              if (ev.showUnread) {
                                withTokenRefresh(() => apiClient.markSystemEventAsRead(ev.id)).catch(() => { /* ignore */ });
                                setSystemEvents((prev) =>
                                  prev.map((e) => (e.id === ev.id ? { ...e, showUnread: false } : e))
                                );
                              }
                            }}
                          >
                            <div className="flex items-start gap-3">
                              {/* Icon */}
                              <img
                                src={getNotificationIcon(ev.event)}
                                alt=""
                                className="flex-shrink-0"
                                style={{ width: 48, height: 48 }}
                              />
                              {/* Content */}
                              <div className="flex-1 min-w-0 flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  {/* Title */}
                                  <div className="text-[13px] font-semibold text-[#111827] capitalize truncate">
                                    {ev.context} · {ev.event}
                                  </div>
                                  {/* One-line description */}
                                  {(() => {
                                    const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
                                    let nameOrBucket = '';
                                    try {
                                      if (ev.jsonData) {
                                        const obj = JSON.parse(ev.jsonData);
                                        nameOrBucket = obj.name || obj.bucket || '';
                                      }
                                    } catch {
                                      /* ignore */
                                    }
                                    const ctx = ev.context.toLowerCase();
                                    const evt = ev.event.toLowerCase();

                                    let action = '';
                                    if (evt === 'created') action = 'successfully created';
                                    else if (evt === 'updated') action = ctx === 'usersettings' ? 'were changed' : 'updated';
                                    else if (evt === 'deleted') action = 'deleted';
                                    else action = evt;

                                    let typeLabel = '';
                                    if (ctx === 'bucket') typeLabel = 'Bucket';
                                    else if (ctx === 'workspace') typeLabel = 'Workspace';
                                    else if (ctx === 'repo') typeLabel = 'Repository';
                                    else if (ctx === 'usersettings') typeLabel = 'User settings';
                                    else if (ctx === 'apikey') typeLabel = 'API Key';
                                    else typeLabel = cap(ctx);

                                    const handleItemClick = (e: React.MouseEvent) => {
                                      e.stopPropagation();
                                      setShowNotifications(false);
                                      if (ctx === 'bucket') {
                                        navigate('/cloud-storage/buckets');
                                        handleTabChange({} as React.SyntheticEvent, TabIndex.StorageUnits);
                                      } else if (ctx === 'repo') {
                                        navigate('/repositories/inventory');
                                        handleTabChange({} as React.SyntheticEvent, TabIndex.StorageRepos);
                                      } else if (ctx === 'apikey') {
                                        navigate('/cloud-storage/overview');
                                        handleTabChange({} as React.SyntheticEvent, TabIndex.Dashboard);
                                      } else if (ctx === 'workspace') {
                                        navigate('/workspace/home');
                                        handleTabChange({} as React.SyntheticEvent, TabIndex.Home);
                                      } else if (ctx === 'organization') {
                                        navigate('/organization');
                                        handleTabChange({} as React.SyntheticEvent, TabIndex.Organization);
                                      } else if (ctx === 'usersettings' || ctx === 'user') {
                                        window.dispatchEvent(new CustomEvent('openProfileModal', { detail: { section: 'Profile' } }));
                                      }
                                    };

                                    return (
                                      <div className="text-[12px] text-[#6B7280] truncate mt-0.5">
                                        {nameOrBucket ? (
                                          <>
                                            {typeLabel}{' '}
                                            <span
                                              onClick={handleItemClick}
                                              style={{
                                                color: '#4766FF',
                                                cursor: 'pointer',
                                                fontWeight: 600,
                                              }}
                                              onMouseEnter={(e) =>
                                                (e.currentTarget.style.textDecoration = 'underline')
                                              }
                                              onMouseLeave={(e) =>
                                                (e.currentTarget.style.textDecoration = 'none')
                                              }
                                            >
                                              "{nameOrBucket}"
                                            </span>{' '}
                                            {action}
                                          </>
                                        ) : (
                                          `${typeLabel} ${action}`
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                                <div className="flex flex-col items-end gap-0.5">
                                  <div className="text-[12px] text-[#6B7280] whitespace-nowrap">
                                    {new Date(ev.timestamp).toLocaleString()}
                                  </div>
                                  {isOrgManager && ev.userId !== currentUserId && ev.userName && (
                                    <div className="text-[11px] text-[#9CA3AF] whitespace-nowrap">
                                      By: {ev.userName}
                                    </div>
                                  )}
                                  {!ev.showUnread && (
                                    <span className="text-[11px] text-[#22C55E] flex items-center gap-0.5">
                                      ✓ Read
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
                {/* Footer: View All Notifications */}
                {isOrgManager && (
                  <div className="shrink-0 border-t border-[#E9EAEB] pt-3 mt-2">
                    <button
                      type="button"
                      className="w-full text-center text-[13px] font-medium text-[#617BFF] hover:text-[#4F63CC] hover:underline py-1"
                      onClick={() => {
                        setShowNotifications(false);
                        navigate('/activity');
                        handleTabChange({} as React.SyntheticEvent, TabIndex.Activity);
                      }}
                    >
                      View All Notifications
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Vertical separator */}
        <div style={{ width: '1px', height: '24px', backgroundColor: '#E5E7EB', margin: '0 4px' }} />

        {/* Right icons: Notifications, Support, Documentation */}
        <div className="flex items-center gap-1">
          {/* Notifications (Bell) */}
          <Tooltip 
            title="Notifications" 
            arrow
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#1F2937',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: '6px 12px',
                  borderRadius: '6px',
                  '& .MuiTooltip-arrow': {
                    color: '#1F2937',
                  },
                },
              },
            }}
          >
            <button
              ref={notificationsButtonRef}
              onClick={() => setShowNotifications(!showNotifications)}
              className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors relative"
            >
              <img src={bellSvg} alt="Notifications" style={{ width: '20px', height: '20px' }} />
              {/* Unread notification badge */}
              {(() => {
                const unreadCount = systemEvents.filter((ev) => ev.showUnread).length;
                if (unreadCount === 0) return null;
                const displayCount = unreadCount > 99 ? '+99' : String(unreadCount);
                return (
                  <span
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      minWidth: '18px',
                      height: '18px',
                      padding: '0 5px',
                      borderRadius: '9px',
                      backgroundColor: '#617BFF',
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                    }}
                  >
                    {displayCount}
                  </span>
                );
              })()}
            </button>
          </Tooltip>

          {/* Support */}
          <Tooltip 
            title="Support" 
            arrow
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#1F2937',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: '6px 12px',
                  borderRadius: '6px',
                  '& .MuiTooltip-arrow': {
                    color: '#1F2937',
                  },
                },
              },
            }}
          >
            <button
              onClick={() => setShowSupportModal(true)}
              className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
            >
              <img src={supportSvg} alt="Support" style={{ width: '20px', height: '20px' }} />
            </button>
          </Tooltip>

          {/* Documentation */}
          <Tooltip 
            title="Documentation" 
            arrow
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#1F2937',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: '6px 12px',
                  borderRadius: '6px',
                  '& .MuiTooltip-arrow': {
                    color: '#1F2937',
                  },
                },
              },
            }}
          >
            <button
              onClick={() => {
                try {
                  window.open('https://docs.flashback.tech/', '_blank', 'noopener,noreferrer');
                } catch {
                  // Ignore errors
                }
              }}
              className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
            >
              <img src={docsSvg} alt="Docs" style={{ width: '20px', height: '20px' }} />
            </button>
          </Tooltip>
        </div>

        {/* User Avatar - Clickable with menu (far right) */}
        <div className="text-left hidden sm:block">
          <Tooltip 
            title={getUserEmail()} 
            arrow
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#1F2937',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: '6px 12px',
                  borderRadius: '6px',
                  '& .MuiTooltip-arrow': {
                    color: '#1F2937',
                  },
                },
              },
            }}
          >
            <div
              onClick={handleClick}
              className="cursor-pointer"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#617BFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px',
                fontWeight: 600,
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#5A6FE8';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#617BFF';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {getUserInitials()}
            </div>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
              sx: {
                backgroundColor: '#1a1b1e',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                mt: 1,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  margin: '4px 8px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                },
                '& .MuiListItemIcon-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  minWidth: 36,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={() => {
              handleClose();
              openProfileModal('Profile');
            }}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={() => handleSignOut()}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
        </div>
        </div>
      </div>

      {/* All Workspaces Dialog */}
      <Dialog
        open={showAllWorkspaces}
        onClose={() => setShowAllWorkspaces(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828' }}>
            All Workspaces ({globalData.workspaces.length})
          </Typography>
          <IconButton onClick={() => setShowAllWorkspaces(false)} size="small">
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {globalData.workspaces.map((w: any) => {
              const isCurrent = globalData.selectedWorkspaceId === w.id;
              return (
                <Box
                  key={w.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: '8px',
                    bgcolor: isCurrent ? '#F0F2FF' : 'transparent',
                    border: isCurrent ? '1px solid #617BFF' : '1px solid transparent',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      bgcolor: isCurrent ? '#F0F2FF' : '#F5F5F5',
                    },
                    '&:hover .select-btn': {
                      opacity: 1,
                      visibility: 'visible',
                    },
                  }}
                >
                  <WorkspacesIcon sx={{ fontSize: 20, color: isCurrent ? '#617BFF' : '#667085' }} />
                  <Typography sx={{ 
                    fontSize: '14px', 
                    fontWeight: isCurrent ? 600 : 400, 
                    color: isCurrent ? '#617BFF' : '#344054',
                    flex: 1,
                  }}>
                    {w.name}
                  </Typography>
                  {isCurrent ? (
                    <Typography sx={{ fontSize: '12px', color: '#617BFF', fontWeight: 500 }}>
                      Current
                    </Typography>
                  ) : (
                    <Box
                      className="select-btn"
                      onClick={() => {
                        handleWorkspaceChange(w.id);
                        setShowAllWorkspaces(false);
                      }}
                      sx={{
                        opacity: 0,
                        visibility: 'hidden',
                        px: 2,
                        py: 0.5,
                        borderRadius: '6px',
                        border: '1px solid #617BFF',
                        color: '#617BFF',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: '#617BFF',
                          color: 'white',
                        },
                      }}
                    >
                      Select
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
          
          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 2, mt: 3, pt: 2, borderTop: '1px solid #E5E7EB' }}>
            <Box
              onClick={() => {
                setShowAllWorkspaces(false);
                try {
                  localStorage.setItem('openOrganizationWorkspaceTab', '1');
                } catch (e) { void 0; }
                handleTabChange({} as React.SyntheticEvent, TabIndex.Organization);
                setTimeout(() => {
                  try { navigate('/organization?tab=workspace'); } catch (e) { void 0; }
                }, 120);
              }}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                py: 1.5,
                px: 2,
                borderRadius: '8px',
                cursor: 'pointer',
                bgcolor: '#617BFF',
                color: 'white',
                fontWeight: 600,
                fontSize: '14px',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: '#5A6FE8',
                },
              }}
            >
              <WorkspacesIcon sx={{ fontSize: 18 }} />
              Manage Workspaces
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Support Modal */}
      <SupportModal 
        open={showSupportModal} 
        onClose={() => setShowSupportModal(false)}
        apiClient={apiClient}
        withTokenRefresh={withTokenRefresh}
        userEmail={authState?.user?.email || ''}
      />

      {/* Upgrade to Organization Modal */}
      <OrganizationUpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        featureName={upgradeFeature === 'organization' ? 'Organization Management' : 'Private Team Chat'}
        onSuccess={() => {
          if (upgradeFeature === 'organization') {
            handleTabChange({} as React.SyntheticEvent, TabIndex.Organization);
          } else {
            handleTabChange({} as React.SyntheticEvent, TabIndex.Chat);
          }
        }}
      />
    </div>
  );
};

export default Header;
